import axios from 'axios';
import { z } from 'zod';

import { strictQuery } from 'utils/strict-query';

import { ChangedDocumentsZod } from 'types/zod/changed-documents';
import { DocumentZod } from 'types/zod/document';
import { DocumentStatusTypeZod } from 'types/zod/document-status-type';
import { DocumentTypeCodeZod } from 'types/zod/document-type-code';
import { QuestionnaireInstanceZod } from 'types/zod/questionnaire-instance';

const DocumentsApi = {
    createDocument: strictQuery({
        In: z.object({
            document_type_code: DocumentZod.shape.document_type_code,
            questionnaire_instance_id: DocumentZod.shape.questionnaire_instance_id,
        }),
        Out: DocumentZod,
        handler: ({ document_type_code, questionnaire_instance_id }) =>
            axios.post('/documents', { document_type_code, questionnaire_instance_id }),
    }),
    deleteScan: strictQuery({
        In: z.object({ id: z.number(), filename: z.string() }),
        Out: z.any(),
        handler: ({ id, filename }) => axios.delete(`/documents/${id}/scans`, { data: { value: filename } }),
    }),
    downloadDocument: strictQuery({
        In: z.object({ path: z.string(), mimeType: z.string().optional() }),
        Out: z.object({ blob: z.any(), headers: z.object({ 'content-disposition': z.string() }) }),
        handler: ({ path, mimeType }) =>
            axios.get(path, { responseType: 'blob', headers: mimeType ? { Accept: mimeType } : {} }),
        mapResponse: ({ data: blob, headers }) => ({ blob, headers }),
    }),
    generate: strictQuery({
        In: z.object({
            questionnaireInstanceId: z.number(),
            changedDocuments: ChangedDocumentsZod.optional(),
        }),
        Out: z.object({ job_id: z.number() }),
        handler: ({ questionnaireInstanceId, changedDocuments }) => {
            const tz = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;
            return axios.post(
                '/generate-doc',
                { questionnaireInstanceId, changedDocuments, tz },
                { params: { withNotifications: true } },
            );
        },
    }),
    getDocumentsByEmail: strictQuery({
        In: z.string(),
        Out: DocumentZod.array(),
        handler: (email) => axios.get('/documents/by-email', { params: { email } }),
    }),
    getDocumentValidity: strictQuery({
        In: z.string(),
        Out: z.object({ status: DocumentStatusTypeZod, name: z.string() }),
        handler: (guid) => axios.get(`/public/documents-guid/${guid}/validity`),
    }),
    getJSONbyDocID: strictQuery({
        In: DocumentZod.shape.document_id,
        Out: z.any(),
        handler: (id) => axios.get(`/documents/${id}/json-for-template`),
    }),
    getSamples: strictQuery({
        In: z.object({ state_code: z.string(), plan_code: z.string() }),
        Out: z.any(),
        handler: ({ state_code, plan_code }) =>
            axios.get('/documents/samples/list', { params: { state_code, plan_code } }),
    }),
    updateStatus: strictQuery({
        In: z.object({ id: z.number(), status: DocumentZod.shape.document_status_code }),
        Out: z.any(),
        handler: ({ id, status }) => axios.put(`/documents/${id}`, { document_status_code: status }),
    }),
    getSignedUrl: strictQuery({
        In: z.object({ path: z.string(), filename: z.string() }),
        Out: z.object({ result: z.string() }),
        handler: ({ path, filename }) => axios.get(`${path}/upload/url/${filename}`),
    }),
    getSignedUrlByDocumentIdAndName: strictQuery({
        In: z.object({ documentId: DocumentZod.shape.document_id, name: z.string() }),
        Out: z.object({ result: z.string() }),
        handler: ({ documentId, name }) => axios.get(`/documents/${documentId}/scans/url`, { params: { name } }),
    }),
    uploadToS3: strictQuery({
        In: z.object({ signedUrl: z.string(), buffer: z.any(), filename: z.string(), file: z.any() }),
        Out: z.any(),
        handler: ({ signedUrl, buffer, filename, file }) =>
            axios.put(signedUrl, buffer, {
                headers: {
                    'Content-Type': file?.split?.(`:`)?.[1]?.split?.(`;`)?.[0],
                    'Content-Disposition': `form-data;filename="${filename}"`,
                },
            }),
    }),
    updateSignedUrl: strictQuery({
        In: z.object({ path: z.string(), url: z.string() }),
        Out: z.unknown(),
        handler: ({ path, url }) => axios.post(`${path}/upload/attach`, { url }),
    }),
    generateDocByCode: strictQuery({
        In: z.object({
            questionnaireInstanceId: QuestionnaireInstanceZod.shape.questionnaire_instance_id,
            tz: z.string(),
            documentType: DocumentTypeCodeZod,
            giftRowId: z.number(),
        }),
        Out: z.object({
            job_id: z.number(),
            created_docs: z.record(DocumentTypeCodeZod),
        }),
        handler: (body) => axios.post('/generate-doc-by-type', body),
    }),
};

export default DocumentsApi;
